import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  X,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Target,
  ArrowRight,
  Map,
  Mountain,
  Zap,
  EyeOff,
} from 'lucide-react';
import { useWizard } from '../contexts/WizardContext';
import { 
  getRecommendationsForPage, 
  getContextualMessage, 
  getQuickActionsForPage,
  type WizardRecommendation 
} from '../lib/wizardConfig';
import { useFeatureFlag, FEATURE_FLAGS } from '../lib/featureFlags';

interface OverlayWizardProps {
  className?: string;
}

export const OverlayWizard: React.FC<OverlayWizardProps> = ({ className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { wizardState, wizardContext, setWizardState } = useWizard();
  
  const isWizardEnabled = useFeatureFlag(FEATURE_FLAGS.OVERLAY_WIZARD);
  const isRecommendationsEnabled = useFeatureFlag(FEATURE_FLAGS.WIZARD_RECOMMENDATIONS);
  const isTutorialEnabled = useFeatureFlag(FEATURE_FLAGS.WIZARD_TUTORIAL);
  const isContextualGuidanceEnabled = useFeatureFlag(FEATURE_FLAGS.WIZARD_CONTEXTUAL_GUIDANCE);
  
  const [isMinimized, setIsMinimized] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialMode, setTutorialMode] = useState<'general' | 'vision'>('general');
  const wizardRef = useRef<HTMLDivElement>(null);

  const pageContext = useMemo(
    () => ({
      visitedPages: wizardContext.visitedPages,
      outcomes: wizardContext.outcomes,
      lastActivity: wizardContext.lastActivity,
    }),
    [wizardContext.visitedPages, wizardContext.outcomes, wizardContext.lastActivity],
  );

  const recommendations = useMemo(() => {
    if (!isRecommendationsEnabled) return [];
    const recs = getRecommendationsForPage(location.pathname, pageContext);
    if (!isTutorialEnabled) {
      return recs.filter((rec) => rec.action.type !== 'tutorial');
    }
    return recs;
  }, [isRecommendationsEnabled, isTutorialEnabled, location.pathname, pageContext]);

  const quickActions = useMemo(() => {
    const actions = getQuickActionsForPage(location.pathname);
    if (!isTutorialEnabled) {
      return actions.filter((action) => action.action !== 'tutorial');
    }
    return actions;
  }, [location.pathname, isTutorialEnabled]);

  const contextualMessage = getContextualMessage(location.pathname, pageContext);

  const handleRecommendationClick = (recommendation: WizardRecommendation) => {
    if (recommendation.action.type === 'navigate') {
      navigate(recommendation.action.target);
      setWizardState({ ...wizardState, isExpanded: false });
    } else if (recommendation.action.type === 'tutorial') {
      if (isTutorialEnabled) startTutorial();
    }
  };

  const toggleExpanded = () => {
    setWizardState({ ...wizardState, isExpanded: !wizardState.isExpanded });
  };

  const toggleMinimized = () => {
    setIsMinimized(!isMinimized);
  };

  const startTutorial = () => {
    setShowTutorial(true);
    setWizardState({ ...wizardState, isGuiding: true, totalSteps: 5 });
  };

  const handleQuickAction = (action: string) => {
    if (action === 'tutorial') {
      if (isTutorialEnabled) startTutorial();
      return;
    }
    navigate(action);
    setWizardState({ ...wizardState, isExpanded: false });
  };

  useEffect(() => {
    const handleTriggerWizard = (event: CustomEvent) => {
      if (!isTutorialEnabled) return;
      const { purpose } = event.detail;
      if (purpose === 'teach-vision') {
        setTutorialMode('vision');
        setShowTutorial(true);
        setWizardState((prev) => ({ ...prev, isVisible: true, isExpanded: true, isGuiding: true }));
      }
    };

    window.addEventListener('triggerWizard', handleTriggerWizard as EventListener);
    return () => window.removeEventListener('triggerWizard', handleTriggerWizard as EventListener);
  }, [isTutorialEnabled, setWizardState]);

  useEffect(() => {
    if (!isTutorialEnabled) setShowTutorial(false);
  }, [isTutorialEnabled]);

  if (!wizardState.isVisible || !isWizardEnabled) return null;

  return (
    <>
      {/* Main Wizard Container */}
      <div
        ref={wizardRef}
        className={`
          fixed bottom-6 right-6 z-50 transition-all duration-300 ease-in-out
          ${isMinimized ? 'scale-75 opacity-70' : 'scale-100 opacity-100'}
          ${className}
        `}
      >
        {!isMinimized && (
          <div
            className={`
              bg-[var(--bg-raised)]  shadow-2xl border-2 border-primary-200
              transition-all duration-300 ease-in-out overflow-hidden
              ${wizardState.isExpanded ? 'w-96 h-96' : 'w-80 h-16'}
            `}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[var(--bg-raised)]/20 flex items-center justify-center">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Knowledge Navigator</h3>
                  <p className="text-xs text-primary-100">Your AI Guide</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleMinimized}
                  className="p-1.5 hover:bg-[var(--bg-raised)]/20 transition-colors"
                  title="Minimize"
                >
                  <EyeOff className="h-4 w-4" />
                </button>
                <button
                  onClick={toggleExpanded}
                  className="p-1.5 hover:bg-[var(--bg-raised)]/20 transition-colors"
                  title={wizardState.isExpanded ? "Collapse" : "Expand"}
                >
                  {wizardState.isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => setWizardState(prev => ({ ...prev, isVisible: false }))}
                  className="p-1.5 hover:bg-[var(--bg-raised)]/20 transition-colors"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            {wizardState.isExpanded && (
              <div className="p-4 h-full overflow-y-auto">
                {/* Contextual Message */}
                {isContextualGuidanceEnabled && (
                  <div className="mb-4 p-3 bg-primary-50 border border-primary-200">
                    <p className="text-sm text-primary-800">
                      {contextualMessage}
                    </p>
                  </div>
                )}

                {/* Recommendations */}
                {isRecommendationsEnabled && recommendations.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-[var(--mustard)]" />
                      Recommended Next Steps
                    </h4>
                    <div className="space-y-2">
                      {recommendations.map((rec) => {
                        const RecIcon = rec.icon;
                        return (
                          <button
                            key={rec.id}
                            onClick={() => handleRecommendationClick(rec)}
                            className={`
                              w-full p-3  border-2 text-left transition-all
                              hover:shadow-md group
                              ${rec.priority === 'high' 
                                ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] hover:bg-[var(--mustard-dim)]' 
                                : rec.priority === 'medium'
                                ? 'border-primary-200 bg-primary-50 hover:bg-primary-100'
                                : 'border-[var(--rule)] bg-[var(--surface)] hover:bg-[var(--hover-tint)]'
                              }
                            `}
                          >
                            <div className="flex items-start gap-3">
                              <RecIcon className={`h-5 w-5 mt-0.5 text-${rec.color}-600 flex-shrink-0`} />
                              <div className="flex-1 min-w-0">
                                <h5 className="font-medium text-charcoal-900 text-sm mb-1">
                                  {rec.title}
                                </h5>
                                <p className="text-xs text-charcoal-600 mb-2">
                                  {rec.description}
                                </p>
                                <div className="flex items-center gap-2">
                                  <span className={`
                                    px-2 py-1 rounded-full text-xs font-medium
                                    ${rec.priority === 'high' 
                                      ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' 
                                      : rec.priority === 'medium'
                                      ? 'bg-primary-200 text-primary-800'
                                      : 'bg-[var(--hover-tint)] text-[var(--text-pri)]'
                                    }
                                  `}>
                                    {rec.priority} priority
                                  </span>
                                  <ArrowRight className={`h-3 w-3 text-${rec.color}-600 group-hover:translate-x-1 transition-transform`} />
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                {quickActions.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-primary-500" />
                      Quick Actions
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {quickActions.map((action) => {
                        const ActionIcon = action.icon;
                        return (
                          <button
                            key={action.id}
                            onClick={() => handleQuickAction(action.action)}
                            className="p-2 border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors text-center"
                          >
                            <ActionIcon className="h-4 w-4 mx-auto mb-1 text-[var(--text-sec)]" />
                            <span className="text-xs text-[var(--text-sec)]">{action.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Journey Stats */}
                <div className="bg-[var(--surface)] p-3">
                  <h4 className="font-semibold text-charcoal-900 mb-2 text-sm">Your Journey</h4>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-charcoal-600">Pages Explored</div>
                      <div className="font-semibold text-charcoal-900">
                        {wizardContext.visitedPages.length}
                      </div>
                    </div>
                    <div>
                      <div className="text-charcoal-600">Outcomes Tracked</div>
                      <div className="font-semibold text-charcoal-900">
                        {wizardContext.outcomes.length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Minimized State */}
        {isMinimized && (
          <button
            onClick={toggleMinimized}
            className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
          >
            <Sparkles className="h-6 w-6 group-hover:rotate-12 transition-transform" />
          </button>
        )}
      </div>

      {/* Tutorial Overlay */}
      {isTutorialEnabled && showTutorial && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--bg-raised)] p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-charcoal-900">
                {tutorialMode === 'vision' ? 'Vision Tutorial' : 'Quick Tutorial'}
              </h3>
              <button
                onClick={() => {
                  setShowTutorial(false);
                  setTutorialMode('general');
                }}
                className="p-1 hover:bg-[var(--hover-tint)] transition-colors"
              >
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>
            
            {tutorialMode === 'vision' ? (
              <div className="space-y-4">
                <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4">
                  <h4 className="font-semibold text-[var(--mustard)] mb-2">The Peak Priorities Vision</h4>
                  <p className="text-[var(--mustard)] text-sm leading-relaxed">
                    This system represents a comprehensive approach to prioritization and business effectiveness. 
                    Let me show you the key components that make this vision powerful.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-600 font-bold text-sm">1</span>
                    </div>
                    <div>
                      <h5 className="font-semibold text-charcoal-900">Four-Layer MOF Architecture</h5>
                      <p className="text-charcoal-700 text-sm">M3 Metadata → M2 Models → M1 Templates → M0 Instances</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sage-600 font-bold text-sm">2</span>
                    </div>
                    <div>
                      <h5 className="font-semibold text-charcoal-900">Platform Engines</h5>
                      <p className="text-charcoal-700 text-sm">Infrastructure that powers the entire knowledge system</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-[var(--mustard-dim)] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[var(--mustard)] font-bold text-sm">3</span>
                    </div>
                    <div>
                      <h5 className="font-semibold text-charcoal-900">Development Lab</h5>
                      <p className="text-charcoal-700 text-sm">Experimental features and cutting-edge capabilities</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-primary-50 border border-primary-200 p-4">
                  <p className="text-primary-800 text-sm">
                    <strong>Key Insight:</strong> This isn't just a tool—it's a complete knowledge system 
                    designed to help organizations achieve clarity, alignment, and confident action.
                  </p>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowTutorial(false);
                      setTutorialMode('general');
                    }}
                    className="flex-1 px-4 py-2 bg-charcoal-100 text-charcoal-700 hover:bg-charcoal-200 transition-colors text-sm font-medium"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      navigate('/elevation/about');
                      setShowTutorial(false);
                      setTutorialMode('general');
                    }}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    Explore Overview
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-charcoal-600">
                  Welcome to the Peak Priorities Knowledge System! This wizard will help you navigate and get the most out of your learning journey.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-2 bg-primary-50">
                    <Target className="h-4 w-4 text-primary-600" />
                    <span className="text-sm text-primary-800">Start at Mission Control to choose your journey</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-sage-50">
                    <Map className="h-4 w-4 text-sage-600" />
                    <span className="text-sm text-sage-800">Explore the Island to discover capabilities</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 bg-[var(--mustard-dim)]">
                    <Mountain className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm text-[var(--mustard)]">Climb the Mountain to track progress</span>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowTutorial(false)}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    Got it!
                  </button>
                  <button
                    onClick={() => {
                      setShowTutorial(false);
                      navigate('/mission-control');
                    }}
                    className="flex-1 px-4 py-2 border border-primary-600 text-primary-600 hover:bg-primary-50 transition-colors text-sm font-medium"
                  >
                    Start Journey
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
